import axios from "axios";
import { useEffect, useState } from "react";
import { Clock } from "react-bootstrap-icons";

import { ApiConnectionString } from "../../../constants/ApiConntection";

import SettingsCard from "./SettingsCard";
import SectionMessage from "./SectionMessage";

import {
  createEmptyMessage,
  getErrorMessage,
  getMinutesText,
} from "../settingsUtils";

const getActualSessionTime = (profile) =>
  Number(
    profile?.actualSessionTime ??
      profile?.ActualSessionTime ??
      50
  );

const getSessionDefaultTime = (profile) =>
  Number(
    profile?.sessionDefaultTime ??
      profile?.SessionDefaultTime ??
      60
  );

const createAppointmentForm = (profile) => {
  const actualSessionTime = getActualSessionTime(profile);
  const sessionDefaultTime = getSessionDefaultTime(profile);

  return {
    actualSessionTime: String(actualSessionTime),
    bufferTime: String(
      Math.max(sessionDefaultTime - actualSessionTime, 0)
    ),
  };
};

export default function AppointmentSettings({
  profile,
  token,
  onProfileUpdated,
}) {
  const [form, setForm] = useState(() =>
    createAppointmentForm(profile)
  );

  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState(createEmptyMessage);

  useEffect(() => {
    setForm(createAppointmentForm(profile));
  }, [profile]);

  const actualSessionTime = Number(form.actualSessionTime);
  const bufferTime = Number(form.bufferTime);
  const diarySlotTime = actualSessionTime + bufferTime;

  const clearMessage = () => {
    setMessage(createEmptyMessage());
  };

  const setError = (error) => {
    setMessage({
      error,
      success: "",
    });
  };

  const handleChange = (event) => {
    const { name, value } = event.target;

    setForm((current) => ({
      ...current,
      [name]: value,
    }));
  };

  const startEditing = () => {
    clearMessage();
    setIsEditing(true);
  };

  const cancelEditing = () => {
    setForm(createAppointmentForm(profile));
    clearMessage();
    setIsEditing(false);
  };

  const saveChanges = async () => {
    if (!actualSessionTime || actualSessionTime <= 0) {
      setError("Please choose a valid session length.");
      return;
    }

    if (bufferTime < 0) {
      setError("Buffer time cannot be less than 0 minutes.");
      return;
    }

    if (!diarySlotTime || diarySlotTime <= 0) {
      setError("Please choose valid appointment timings.");
      return;
    }

    try {
      setIsSaving(true);
      clearMessage();

      const response = await axios.put(
        `${ApiConnectionString}/api/myProfile/defaultTime`,
        {
          actualSessionTime,
          sessionDefaultTime: diarySlotTime,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const returnedProfile =
        response.data && typeof response.data === "object"
          ? response.data
          : {};

      const updatedActualSessionTime = Number(
        returnedProfile.actualSessionTime ??
          returnedProfile.ActualSessionTime ??
          actualSessionTime
      );

      const updatedSessionDefaultTime = Number(
        returnedProfile.sessionDefaultTime ??
          returnedProfile.SessionDefaultTime ??
          diarySlotTime
      );

      const updatedProfile = {
        ...profile,
        ...returnedProfile,
        actualSessionTime: updatedActualSessionTime,
        sessionDefaultTime: updatedSessionDefaultTime,
      };

      onProfileUpdated(updatedProfile);
      setForm(createAppointmentForm(updatedProfile));
      setIsEditing(false);

      setMessage({
        error: "",
        success: "Appointment defaults updated successfully.",
      });
    } catch (error) {
      console.error(error);

      setError(
        getErrorMessage(
          error,
          "Failed to save appointment settings."
        )
      );
    } finally {
      setIsSaving(false);
    }
  };

  const savedActualSessionTime = getActualSessionTime(profile);

  const savedBufferTime = Math.max(
    getSessionDefaultTime(profile) - savedActualSessionTime,
    0
  );

  return (
    <SettingsCard
      icon={<Clock />}
      title="Appointment defaults"
      description="Set your session length and time kept free afterwards."
    >
      <div className="settings-form">
        <SectionMessage message={message} />

        <div className="settings-grid">
          <div>
            <label htmlFor="settings-session-length">
              Session length
            </label>

            {!isEditing ? (
              <div className="settings-readonly-value">
                {getMinutesText(savedActualSessionTime)}
              </div>
            ) : (
              <select
                id="settings-session-length"
                name="actualSessionTime"
                value={form.actualSessionTime}
                onChange={handleChange}
              >
                <option value="30">30 minutes</option>
                <option value="45">45 minutes</option>
                <option value="50">50 minutes</option>
                <option value="60">60 minutes</option>
                <option value="75">75 minutes</option>
                <option value="90">90 minutes</option>
              </select>
            )}

            <small>How long the client session lasts.</small>
          </div>

          <div>
            <label htmlFor="settings-buffer-time">
              Buffer time after session
            </label>

            {!isEditing ? (
              <div className="settings-readonly-value">
                {getMinutesText(savedBufferTime)}
              </div>
            ) : (
              <select
                id="settings-buffer-time"
                name="bufferTime"
                value={form.bufferTime}
                onChange={handleChange}
              >
                <option value="0">No buffer</option>
                <option value="5">5 minutes</option>
                <option value="10">10 minutes</option>
                <option value="15">15 minutes</option>
                <option value="20">20 minutes</option>
                <option value="25">25 minutes</option>
                <option value="30">30 minutes</option>
              </select>
            )}

            <small>
              Time for notes, admin, overruns or a short break.
            </small>
          </div>
        </div>

        <div className="settings-help-box">
          {actualSessionTime || 0} min session + {bufferTime || 0} min
          buffer = {diarySlotTime || 0} min reserved in your diary.
        </div>

        <div className="settings-actions">
          {!isEditing ? (
            <button type="button" onClick={startEditing}>
              Edit
            </button>
          ) : (
            <>
              <button
                type="button"
                onClick={saveChanges}
                disabled={isSaving}
              >
                {isSaving ? "Saving..." : "Save changes"}
              </button>

              <button
                type="button"
                onClick={cancelEditing}
                disabled={isSaving}
              >
                Cancel
              </button>
            </>
          )}
        </div>
      </div>
    </SettingsCard>
  );
}